class SearchForm extends HTMLElement {
  /**
   * Orden obligatorio en Farmex: filtros -tag primero, texto del cliente al final.
   * Así options[prefix]=last aplica al término buscado (documentación Shopify).
   */
  static removeDiacritics(str) {
    return String(str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Variantes con/sin acento (ej. lidocaina ↔ lidocaína).
   * Una tilde a la vez (no combinatoria): muchas OR agotan el limit de
   * Predictive Search y Shopify puede devolver 0 productos (solo colecciones).
   */
  static expandAccentVariants(term) {
    const input = String(term || '').trim();
    if (!input) return [];
    // Códigos / SKU: no expandir
    if (/^[\d\s\-]+$/.test(input)) return [input];

    const stripped = SearchForm.removeDiacritics(input);
    const variants = new Set([input, stripped]);

    const words = input.split(/\s+/).filter(Boolean);
    // Multi-palabra: solo original + sin acentos
    if (words.length > 1) return [...variants];

    // Solo vocales (no n↔ñ): una posición acentuada por variante
    const vowelAccents = { a: 'á', e: 'é', i: 'í', o: 'ó', u: 'ú' };
    const base = stripped.toLowerCase();
    for (let i = 0; i < base.length; i++) {
      const accent = vowelAccents[base[i]];
      if (!accent) continue;
      variants.add(base.slice(0, i) + accent + base.slice(i + 1));
    }
    return [...variants];
  }

  static buildAccentInsensitiveQuery(term) {
    const trimmed = String(term || '').trim();
    if (!trimmed) return '';
    const variants = SearchForm.expandAccentVariants(trimmed);
    if (variants.length <= 1) return trimmed;

    const unique = [];
    const seen = new Set();
    for (const variant of [trimmed, ...variants]) {
      const key = variant.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(variant);
      // Pocas OR: dejar cupo en resources[limit] para productos
      if (unique.length >= 8) break;
    }
    if (unique.length === 1) return unique[0];
    return `(${unique.join(' OR ')})`;
  }

  static buildStorefrontSearchQuery(rawTerm) {
    let term = (rawTerm || '')
      .trim()
      .replace(/-tag:delete/gi, '')
      .replace(/-tag:persistente/gi, '')
      .replace(/-tag:RM/gi, '')
      .trim();
    if (!term) return '';
    const excludedTags = '-tag:delete -tag:persistente';
    const accentQuery = SearchForm.buildAccentInsensitiveQuery(term);
    return `${excludedTags} ${accentQuery}`.trim();
  }

  static getSearchSubmitUrl(form, rawTerm) {
    const q = SearchForm.buildStorefrontSearchQuery(rawTerm);
    if (!q) return null;
    const u = new URL(form.action);
    u.search = '';
    u.searchParams.set('q', q);
    const prefixInput = form.querySelector('input[name="options[prefix]"]');
    if (prefixInput?.value) {
      u.searchParams.set('options[prefix]', prefixInput.value);
    }
    const unavailableInput = form.querySelector('input[name="options[unavailable_products]"]');
    u.searchParams.set(
      'options[unavailable_products]',
      unavailableInput?.value || 'last'
    );
    return u.toString();
  }

  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (!this.input) return;

    this.form = this.input.form;

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.form.addEventListener('reset', this.onFormReset.bind(this));

    this.input.addEventListener('input', debounce(() => {
      this.onChange();
    }, 300));
  }

  onSubmit(event) {
    event.preventDefault();
    const url = SearchForm.getSearchSubmitUrl(this.form, this.input.value);
    if (!url) return;
    window.location.href = url;
  }

  toggleResetButton() {
    if (!this.resetButton) return;

    this.resetButton.classList.toggle(
      'hidden',
      this.input.value.length === 0
    );
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);
